import { useState, useEffect } from 'react';
import { X, Play, Loader, Clock, Database, Columns } from 'lucide-react';
import type { SavedQuery, QueryExecutionResult } from '../../types/query';
import { queryService } from '../../services/queryService';
import QueryResultViewer from './QueryResultViewer';

interface QueryExecuteModalProps {
  isOpen: boolean;
  onClose: () => void;
  query: SavedQuery | null;
}

export default function QueryExecuteModal({ isOpen, onClose, query }: QueryExecuteModalProps) {
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<QueryExecutionResult | null>(null);

  useEffect(() => {
    if (isOpen && query) {
      setResult(null);
      executeQuery();
    }
  }, [isOpen, query]);

  const executeQuery = async () => {
    if (!query) return;

    setIsExecuting(true);
    try {
      const executionResult = await queryService.executeQuery(query);
      setResult(executionResult);
    } catch (error) {
      setResult({
        success: false,
        data: [],
        columns: [],
        rowCount: 0,
        executionTime: 0,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  if (!isOpen || !query) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{query.name}</h2>
            {query.description && (
              <p className="text-sm text-gray-500 mt-0.5">{query.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3 mb-3">
              <Database className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Query SQL</h4>
                <pre className="text-sm text-gray-600 font-mono whitespace-pre-wrap break-all">
                  {query.sql_query}
                </pre>
              </div>
            </div>

            {query.selected_columns.length > 0 && (
              <div className="flex items-start gap-3 pt-3 border-t border-gray-200">
                <Columns className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Colunas Selecionadas</h4>
                  <div className="flex flex-wrap gap-1">
                    {query.selected_columns.map((col) => (
                      <span key={col} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {isExecuting ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Executando serviço...</p>
            </div>
          ) : result ? (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{result.executionTime}ms</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Database className="w-4 h-4" />
                  <span>{result.rowCount} resultado(s)</span>
                </div>
              </div>
              <QueryResultViewer result={result} />
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            Endpoint: <code className="bg-gray-200 px-2 py-0.5 rounded">{queryService.generateEndpointUrl(query.endpoint_slug)}</code>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Fechar
            </button>
            <button
              onClick={executeQuery}
              disabled={isExecuting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
            >
              {isExecuting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Executar Novamente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
